import React from "react";
import { createTheme, ThemeProvider, CssBaseline } from "@mui/material";
import useDarkMode from "use-dark-mode";

// Superficies "liquid glass" alineadas con src/styles/helpers.sass
// ($n8 #0F1219 fondo, $n7 #1B202B superficie, $shades1 #A6ADBB texto secundario).
// Los popups de MUI (Dialog/Menu/Popover) van en portal al body, así que su
// backdrop-filter sí difumina la página entera; los Paper embebidos usan la
// misma receta que el mixin liquidGlass.
const GLASS_DARK = {
  background: "linear-gradient(180deg, rgba(255,255,255,0.045) 0%, rgba(255,255,255,0) 45%), rgba(23, 28, 39, 0.62)",
  backdropFilter: "blur(24px) saturate(1.8)",
  WebkitBackdropFilter: "blur(24px) saturate(1.8)",
  border: "1px solid rgba(255, 255, 255, 0.08)",
  boxShadow:
    "inset 0 1px 0 rgba(255,255,255,0.09), 0 16px 48px rgba(3,5,10,0.5), 0 2px 8px rgba(3,5,10,0.3)",
};

const GLASS_DARK_FLOATING = {
  background: "linear-gradient(180deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0) 45%), rgba(23, 28, 39, 0.9)",
  backdropFilter: "blur(28px) saturate(1.8)",
  WebkitBackdropFilter: "blur(28px) saturate(1.8)",
  border: "1px solid rgba(255, 255, 255, 0.1)",
  boxShadow:
    "inset 0 1px 0 rgba(255,255,255,0.1), 0 24px 64px rgba(3,5,10,0.55), 0 4px 12px rgba(3,5,10,0.3)",
};

const GLASS_LIGHT = {
  background: "rgba(255, 255, 255, 0.94)",
  backdropFilter: "blur(24px) saturate(1.8)",
  WebkitBackdropFilter: "blur(24px) saturate(1.8)",
  border: "1px solid rgba(0, 0, 0, 0.08)",
  boxShadow:
    "inset 0 1px 0 rgba(255,255,255,0.85), 0 8px 32px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.03)",
};

const getDesignTokens = (mode) => {
  const dark = mode === "dark";
  return {
    palette: {
      mode,
      ...(dark
        ? {
            background: {
              default: "#0F1219",
              paper: "#1B202B",
            },
            text: {
              primary: "#FCFCFC",
              secondary: "#A6ADBB",
            },
            divider: "rgba(255, 255, 255, 0.08)",
            primary: { main: "#2A85FF" },
            success: { main: "#83BF6E" },
            error: { main: "#FF6A55" },
          }
        : {
            background: {
              default: "#F4F4F4",
              paper: "#FFFFFF",
            },
            text: {
              primary: "#1B202B",
              secondary: "#6F767E",
            },
            divider: "#EFEFEF",
            primary: { main: "#2A85FF" },
            success: { main: "#83BF6E" },
            error: { main: "#FF6A55" },
          }),
    },
    typography: {
      fontFamily: "'Inter', sans-serif",
      button: {
        textTransform: "none",
        fontWeight: 600,
      },
    },
    shape: {
      borderRadius: 12,
    },
    components: {
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            ...(dark ? GLASS_DARK : GLASS_LIGHT),
            borderRadius: 16,
          },
          outlined: dark
            ? {
                background: "rgba(255, 255, 255, 0.03)",
                backdropFilter: "none",
                WebkitBackdropFilter: "none",
                border: "1px solid rgba(255, 255, 255, 0.08)",
                boxShadow: "none",
              }
            : {
                background: "#FFFFFF",
                backdropFilter: "none",
                WebkitBackdropFilter: "none",
                boxShadow: "none",
              },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 20,
            ...(dark ? GLASS_DARK_FLOATING : GLASS_LIGHT),
          },
        },
      },
      MuiBackdrop: {
        styleOverrides: {
          root: {
            backgroundColor: dark ? "rgba(5, 7, 12, 0.6)" : "rgba(244, 244, 244, 0.75)",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
            "&.MuiBackdrop-invisible": {
              backgroundColor: "transparent",
              backdropFilter: "none",
              WebkitBackdropFilter: "none",
            },
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            borderRadius: 14,
            ...(dark ? GLASS_DARK_FLOATING : GLASS_LIGHT),
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          paper: {
            borderRadius: 14,
            ...(dark ? GLASS_DARK_FLOATING : GLASS_LIGHT),
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            margin: "2px 6px",
            "&:hover": {
              backgroundColor: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
            },
            "&.Mui-selected": {
              backgroundColor: dark ? "rgba(42,133,255,0.18)" : "rgba(42,133,255,0.1)",
              "&:hover": {
                backgroundColor: dark ? "rgba(42,133,255,0.24)" : "rgba(42,133,255,0.14)",
              },
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: dark
              ? "1px solid rgba(255, 255, 255, 0.06)"
              : "1px solid #EFEFEF",
          },
          head: {
            color: dark ? "#A6ADBB" : "#6F767E",
          },
          stickyHeader: {
            backgroundColor: dark ? "#161B26" : "#FFFFFF",
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            "&.MuiTableRow-hover:hover": {
              backgroundColor: dark ? "rgba(255,255,255,0.035)" : "rgba(0,0,0,0.02)",
            },
          },
        },
      },
      MuiTableSortLabel: {
        styleOverrides: {
          root: {
            "&:hover": { color: dark ? "#FCFCFC" : "#1B202B" },
            "&.Mui-active": {
              color: dark ? "#FCFCFC" : "#1B202B",
              "& .MuiTableSortLabel-icon": { color: "#2A85FF" },
            },
          },
        },
      },
      MuiTablePagination: {
        styleOverrides: {
          root: {
            color: dark ? "#A6ADBB" : "#6F767E",
            borderTop: dark ? "1px solid rgba(255,255,255,0.06)" : "1px solid #EFEFEF",
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            padding: "8px 20px",
            fontWeight: 700,
            fontSize: 14,
          },
          containedPrimary: {
            boxShadow: "none",
            "&:hover": {
              boxShadow: "0 4px 14px rgba(42, 133, 255, 0.35)",
            },
          },
          containedError: {
            boxShadow: "none",
            "&:hover": {
              boxShadow: "0 4px 14px rgba(255, 106, 85, 0.35)",
            },
          },
          outlined: dark
            ? {
                borderColor: "rgba(255, 255, 255, 0.16)",
                "&:hover": {
                  borderColor: "rgba(255, 255, 255, 0.32)",
                  backgroundColor: "rgba(255, 255, 255, 0.04)",
                },
              }
            : {},
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            "&:hover": {
              backgroundColor: dark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.04)",
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            backgroundColor: dark ? "rgba(39, 44, 56, 0.5)" : "rgba(0, 0, 0, 0.02)",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: dark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.12)",
              transition: "border-color .2s",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: dark ? "rgba(255, 255, 255, 0.22)" : "rgba(0, 0, 0, 0.24)",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "rgba(42, 133, 255, 0.7)",
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: dark ? "#A6ADBB" : "#6F767E",
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontWeight: 600,
          },
          filledDefault: {
            backgroundColor: dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
          },
          outlined: {
            borderColor: dark ? "rgba(255,255,255,0.16)" : undefined,
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 10,
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            backgroundColor: dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: dark ? "rgba(23, 28, 39, 0.95)" : "rgba(27, 32, 43, 0.92)",
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 8,
            fontSize: 12,
            fontWeight: 500,
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: dark ? "rgba(255,255,255,0.08)" : "#EFEFEF",
          },
        },
      },
    },
  };
};

const MuiThemeProvider = ({ children }) => {
  const darkMode = useDarkMode(false);
  const mode = darkMode.value ? "dark" : "light";
  const theme = React.useMemo(() => createTheme(getDesignTokens(mode)), [mode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline enableColorScheme />
      {children}
    </ThemeProvider>
  );
};

export default MuiThemeProvider;
